import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { cx, css } from '@emotion/css';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Auth } from 'aws-amplify';
import { SubscriptionContext } from '../../reducers/SubscriptionContext';
import { LocationContext } from '../../reducers/LocationContext';

// Import vector logos
import spellBowlImg from '../../assets/gen_images/batchesicon.png';
import mathsImg from '../../assets/gen_images/mathematics.png';
import generalMathImg from '../../assets/gen_images/general_math.png';
import scienceBowlImg from '../../assets/gen_images/sciencebowl_card.png';
import revImg from '../../assets/gen_images/rev.png';

// Input styles
const inputStyles = {
  width: 72,
  minWidth: 60,
  flexShrink: 0,
  textAlign: 'center',
  fontWeight: 'bold',
  padding: '6px 8px',
  boxSizing: 'border-box',
  border: '1.5px solid rgba(241, 102, 2, 0.18)',
  borderRadius: '8px',
  backgroundColor: '#fff',
  transition: 'all 0.2s',
  fontSize: '0.82rem',
  color: '#382110',
};

const cardStyles = css`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0px 10px 30px rgba(241, 102, 2, 0.05);
  border: 1.5px solid rgba(241, 102, 2, 0.18);
  background: linear-gradient(to bottom, #fffcf9 0%, #fff3e6 65%, #ffe6cc 100%);
  padding: 2.2rem 1.8rem 1.8rem 1.8rem;
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  text-align: center;
  align-items: center;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 25px 50px rgba(241, 102, 2, 0.12), 0 0 35px rgba(241, 102, 2, 0.06);
    border-color: rgba(241, 102, 2, 0.4);
    background: linear-gradient(to bottom, #fffcf9 0%, #fff0e0 55%, #ffd4a8 100%);

    .card-logo-container {
      border-color: #f16602;
      box-shadow: 0 15px 40px rgba(241, 102, 2, 0.2);
      transform: scale(1.05);
      
      img {
        transform: scale(1.1);
      }
    }

    .logo-glow {
      transform: scale(1.2);
      opacity: 0.8;
    }
  }

  .card-logo-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 0.5rem;
    margin-bottom: 1.4rem;
    position: relative;
    width: 100%;
  }

  .card-logo-container {
    width: 140px;
    height: 140px;
    border-radius: 30px;
    overflow: hidden;
    background: #ffffff;
    border: 2px solid rgba(241, 102, 2, 0.18);
    box-shadow: 0 10px 35px rgba(241, 102, 2, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    z-index: 2;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 10px;
      transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);
    }
  }

  .logo-glow {
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(241, 102, 2, 0.15) 0%, transparent 70%);
    filter: blur(8px);
    z-index: 1;
    pointer-events: none;
    transition: all 0.4s ease;
  }

  .card-title {
    font-size: 1.35rem;
    font-weight: 800;
    color: #382110;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    margin: 0 0 1.2rem 0;
    line-height: 1.25;
  }

  .shuffle-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 1.2rem;
    font-weight: 700;
    font-size: 0.8rem;
    color: #594436;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    /* Styling switch */
    .switch {
      position: relative;
      display: inline-block;
      width: 44px;
      height: 24px;
    }

    .switch input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    .slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(241, 102, 2, 0.15);
      transition: .4s;
      border: 1px solid rgba(241, 102, 2, 0.18);
    }

    .slider:before {
      position: absolute;
      content: "";
      height: 16px;
      width: 16px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    input:checked + .slider {
      background-color: #f16602;
      border-color: #f16602;
    }

    input:checked + .slider:before {
      transform: translateX(20px);
    }

    .slider.round {
      border-radius: 34px;
    }

    .slider.round:before {
      border-radius: 50%;
    }
  }

  .control-row {
    width: 100%;
    margin-bottom: 1.2rem;
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
  }

  .ct-input-label {
    font-size: 0.78rem;
    font-weight: 800;
    color: #f16602;
    text-transform: uppercase;
  }

  .select-wrapper {
    width: 100%;
    margin-bottom: 1rem;

    select {
      width: 100%;
      height: 44px;
      border-radius: 12px;
      border: 1.5px solid rgba(241, 102, 2, 0.18);
      background-color: #ffffff;
      padding: 0 16px;
      font-weight: 700;
      font-size: 0.82rem;
      color: #382110;
      cursor: pointer;
      outline: none;
      transition: all 0.3s ease;

      &:focus, &:hover {
        border-color: #f16602;
        box-shadow: 0 0 0 4px rgba(241, 102, 2, 0.05);
      }
    }
  }

  .time-display {
    font-size: 0.8rem;
    color: #594436;
    font-weight: 700;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .btn-primary {
    cursor: pointer;
    padding: 0.85rem;
    width: 100%;
    background: #f16602;
    font-size: 0.82rem;
    font-weight: 700;
    color: #ffffff;
    border: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-radius: 9999px;
    box-shadow: 0 4px 15px rgba(241, 102, 2, 0.25);
    transition: all 0.3s ease;
    text-align: center;
    text-decoration: none;
    display: block;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(241, 102, 2, 0.35);
      color: #ffffff;
      text-decoration: none;
    }
  }
`;

function PracticeCard(props) {
  const { name, totalquestions, category } = props.event;
  const event_copy = props.event;

  const timeOptions = [0, 5, 10, 15, 30, 45, 60, 90, 120, 180, 300];

  const formatTimeDisplay = (seconds) => {
    if (seconds === 0) return 'No time limit';
    if (seconds >= 60) {
      const minutes = seconds / 60;
      return `${minutes === Math.floor(minutes) ? minutes : minutes.toFixed(1)} min`;
    }
    return `${seconds} sec`;
  };
  
  const formatTotalTime = (seconds) => {
    if (seconds === 0) return 'No time limit';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 0 && s > 0) return `${m}m ${s}s`;
    if (m > 0) return `${m}m`;
    return `${s}s`;
  };

  const [value, setValue] = useState([1, totalquestions]);
  const [inputStart, setInputStart] = useState('1');
  const [inputEnd, setInputEnd] = useState(String(totalquestions));
  const [random, setRandom] = useState(true);
  const [timePerQuestion, setTimePerQuestion] = useState(60);

  // Sync string inputs with slider
  useEffect(() => {
    setInputStart(String(value[0]));
    setInputEnd(String(value[1]));
  }, [value[0], value[1]]);

  useEffect(() => {
    Auth.currentAuthenticatedUser().catch(() => { });
  }, []);

  function randomme() {
    setRandom(prev => {
      const newVal = !prev;
      event_copy.randomize = newVal;
      return newVal;
    });
  }

  function handleTimeChange(time) {
    const parsedTime = parseInt(time);
    setTimePerQuestion(parsedTime);
    event_copy.timePerQuestion = parsedTime;
    event_copy.time = parsedTime;
  }

  function handleChange(change) {
    setValue(change);
    event_copy.start_limit = change[0] - 1;
    event_copy.end_limit = change[1] - 1;
    event_copy.randomize = random;
    event_copy.timePerQuestion = parseInt(timePerQuestion);
    event_copy.time = parseInt(timePerQuestion);
  }

  function handleStartInputChange(e) {
    setInputStart(e.target.value);
  }

  function handleStartBlur() {
    let val = parseInt(inputStart, 10);
    if (isNaN(val)) val = 1;
    if (val < 1) val = 1;
    if (val >= value[1]) val = value[1] - 1;
    setValue([val, value[1]]);
    setInputStart(String(val));
  }

  function handleEndInputChange(e) {
    setInputEnd(e.target.value);
  }

  function handleEndBlur() {
    let val = parseInt(inputEnd, 10);
    if (isNaN(val)) val = value[0];
    if (val < value[0]) val = value[0];
    if (val > totalquestions) val = totalquestions;
    setValue([value[0], val]);
    setInputEnd(String(val));
  }

  const numQuestions = value[1] - value[0] + 1;
  const totalTimeInSeconds = timePerQuestion === 0 ? 0 : timePerQuestion * numQuestions;

  // Resolve vector logo dynamically
  const catLower = (category || '').toLowerCase();
  let logoSrc = null;
  if (catLower.includes('spell')) {
    logoSrc = spellBowlImg;
  } else if (catLower.includes('mathbowl')) {
    logoSrc = mathsImg;
  } else if (catLower.includes('math') || catLower.includes('algebra')) {
    logoSrc = generalMathImg;
  } else if (catLower.includes('science')) {
    logoSrc = scienceBowlImg;
  } else {
    logoSrc = revImg;
  }

  return (
    <div className={cx(cardStyles)}>
      {/* Center Logo Container with Glow */}
      <div className="card-logo-wrapper">
        <div className="card-logo-container">
          <img src={logoSrc} alt={name} />
        </div>
        <div className="logo-glow" />
      </div>

      {/* Card Content */}
      <h3 className="card-title">{name}</h3>

      {/* Shuffle switch */}
      {(['SpellBowl', 'Spanish', 'Invitational', 'ScienceBowl'].includes(category)) && (
        <div className="shuffle-container">
          <span>Shuffle Questions</span>
          <label className="switch">
            <input type="checkbox" onChange={randomme} defaultChecked={true} />
            <span className="slider round"></span>
          </label>
        </div>
      )}

      {/* Range controls row */}
      <div className="control-row">
        <span className="ct-input-label">Range:</span>
        <input
          type="number"
          min={1}
          max={value[1] - 1}
          value={inputStart}
          onChange={handleStartInputChange}
          onBlur={handleStartBlur}
          style={inputStyles}
        />
        <Slider
          trackStyle={{ backgroundColor: '#f16602', height: 6 }}
          handleStyle={{ borderColor: '#f16602', height: 16, width: 16, marginTop: -5, backgroundColor: '#ffffff' }}
          railStyle={{ backgroundColor: 'rgba(241, 102, 2, 0.15)', height: 6 }}
          value={value}
          range={[1, totalquestions]}
          min={1}
          max={totalquestions}
          onChange={handleChange}
          style={{ flex: '1 1 0', minWidth: 60 }}
        />
        <input
          type="number"
          min={value[0] + 1}
          max={totalquestions}
          value={inputEnd}
          onChange={handleEndInputChange}
          onBlur={handleEndBlur}
          style={inputStyles}
        />
      </div>

      {/* Timer Select Dropdown */}
      <div className="select-wrapper">
        <select
          name="timePerQuestion"
          value={timePerQuestion}
          onChange={(e) => handleTimeChange(e.target.value)}
          required
        >
          <option value="" disabled>Time per Question</option>
          {timeOptions.map((time) => (
            <option key={time} value={time}>
              {formatTimeDisplay(time)}
            </option>
          ))}
        </select>
      </div>

      {/* Total Session Time Display */}
      <div className="time-display">
        <span>⏱️ Session:</span>
        <span style={{ color: '#f16602' }}>{formatTotalTime(totalTimeInSeconds)}</span>
      </div>

      {/* Action Button */}
      <Link 
        to={{ pathname: '/practicequiz', state: event_copy }}
        className="btn-primary"
        onClick={() => {
          event_copy.start_limit = value[0] - 1;
          event_copy.end_limit = value[1] - 1;
          event_copy.randomize = random;
          event_copy.timePerQuestion = parseInt(timePerQuestion);
          event_copy.time = parseInt(timePerQuestion);
        }}
      >
        Start Practice
      </Link>
    </div>
  );
}

export default PracticeCard;
